
import { Options, prop, Vue } from "vue-class-component";
import { debounce } from "lodash";
import Skeleton from "@/app/ui/components/skeleton/index.vue";
import CheckBox from "@/app/ui/components/check-box/index.vue";
import { ellipsisString } from "@/app/infrastructures/misc/Utils";

class Props {
  options = prop<Array<any>>({
    default: [],
    type: Array
  });
  errorDataShow = prop<Array<any>>({
    default: [],
    type: Array
  });
  optionName = prop<string>({
    default: "Item",
    type: String
  });
  modelValue = prop<Array<any>>({
    default: [],
    type: Array
  });
  isDisabled = prop<boolean>({
    default: false,
    type: Boolean
  });
  disableBackground = prop<boolean>({
    default: false,
    type: Boolean
  });
  allEnabled = prop<boolean>({
    default: false,
    type: Boolean
  });
  isLoading = prop<boolean>({
    default: false,
    type: Boolean
  });
  isError = prop<boolean>({
    default: false,
    type: Boolean
  });
  placeholder = prop<string>({
    default: "",
    type: String
  });
  keyName = prop<string>({
    default: "name",
    type: String
  });
  keyValue = prop<string>({
    default: "value",
    type: String
  });
  modalTitle = prop<string>({
    default: "",
    type: String
  });
  maxItemDisplayed = prop<number>({
    default: 999,
    type: Number
  });
  closeOnSelecting = prop<boolean>({
    default: false,
    type: Boolean
  });
  error = prop<boolean>({
    default: false,
    type: Boolean
  });
  errorCaption = prop<string>({
    default: "",
    type: String
  });
  serverSide = prop<boolean>({
    default: true,
    type: Boolean
  });
  showReset = prop<boolean>({
    default: false,
    type: Boolean
  });
  showSelected = prop<boolean>({
    default: true,
    type: Boolean
  });
  showSelectedInInput = prop<boolean>({
    default: false,
    type: Boolean
  });
  hideInput = prop<boolean>({
    default: false,
    type: Boolean
  });
  ableToDeleteInModal = prop<boolean>({
    default: false,
    type: Boolean
  });
  displayValue = prop<boolean>({
    default: false,
    type: Boolean
  });
  customDisplayAll = prop<string>({
    default: "Pilih Semua",
    type: String
  });
  noteForBosspack = prop<boolean>({
    default: false,
    type: Boolean
  });
  disabledBosspack = prop<boolean>({
    default: false,
    type: Boolean
  });
  customSelectedItem = prop<boolean>({
    default: false,
    type: Boolean
  });
  useInfiniteScroll = prop<boolean>({
    default: false,
    type: Boolean
  });
  showSelectAll = prop<boolean>({
    default: true,
    type: Boolean
  });
  selectAllText = prop<string>({
    default: "Pilih Semua",
    type: String
  });
  selectAllClass = prop<string>({
    default: "font-bold",
    type: String
  });
  maxDisplayedChips = prop<number>({
    default: 2,
    type: Number
  });
  hasMoreData = prop<boolean>({
    default: true,
    type: Boolean
  });
  scrollDebounceMs = prop<number>({
    default: 300,
    type: Number
  });
  selectAllOption = prop<boolean>({
    default: true,
    type: Boolean
  });
  allSelectedValueShow = prop<boolean>({
    default: false,
    type: Boolean
  });
  preserveAllSelectionOnOptionsChange = prop<boolean>({
    default: true,
    type: Boolean
  });
}

@Options({
  emits: [
    "focus",
    "blur",
    "change",
    "filter",
    "reset",
    "update:modelValue",
    "update:isError",
    "remove",
    "scrolled",
    "load-more"
  ],
  components: {
    Skeleton,
    CheckBox
  },
  directives: {
    "click-outside": {
      beforeMount: function(el: any, binding: any) {
        const ourClickEventHandler = (event: any) => {
          if (!el.contains(event.target) && el !== event.target) {
            binding.value(event);
          }
        };
        el.__vueClickEventHandler__ = ourClickEventHandler;
        document.addEventListener("click", ourClickEventHandler);
      },
      unmounted: function(el: any) {
        document.removeEventListener("click", el.__vueClickEventHandler__);
      }
    }
  },
  watch: {
    modelValue() {
      this.$emit("update:isError", !this.modelValue.length);
    },
    filterSearch() {
      if (this.filterSearch && this.hasMoreData === false) {
        this.$emit("filter", this.filterSearch);
      }
    },
    options: {
      handler() {
        if (!this.useInfiniteScroll) return;
        if (!this.validateAllOnNextOptionsChange) return;
        if (this.preserveAllSelectionOnOptionsChange) return;
        const onlyAllOption =
          Array.isArray(this.options) &&
          this.options.length === 1 &&
          (String(
            this.options[0][this.keyValue] ?? this.options[0]
          ).toLowerCase() === "all" ||
            String(this.options[0][this.keyName] ?? "").toLowerCase() ===
              "semua");

        const selectedIsOnlyAll =
          Array.isArray(this.modelValue) &&
          this.modelValue.length === 1 &&
          (String(
            this.modelValue[0][this.keyValue] ?? this.modelValue[0]
          ).toLowerCase() === "all" ||
            String(this.modelValue[0][this.keyName] ?? "").toLowerCase() ===
              "semua");

        if (onlyAllOption || selectedIsOnlyAll) {
          this.validateAllOnNextOptionsChange = false;
          return;
        }

        if (!this.isAllSelected && this.isAll()) {
          const allIndex = this.modelValue.findIndex(
            (el: any) =>
              String(el[this.keyValue] || el).toLowerCase() === "all" ||
              String(el[this.keyName] || "").toLowerCase() === "semua"
          );
          if (allIndex !== -1) {
            this.modelValue.splice(allIndex, 1);
            this.$emit("change", this.modelValue);
          }
        }
        this.validateAllOnNextOptionsChange = false;
      },
      deep: true
    }
  }
})
export default class MultipleSelectSearchVirtualScroll extends Vue.with(Props) {
  // logic open close menu
  isChevron = false;
  isOpenMenu = false;
  isLoadingMore = false;
  showAllSelectedItems = false;
  validateAllOnNextOptionsChange = false;

  // Debounced scroll handler
  debouncedScrollHandler: any = null;

  created() {
    // Initialize debounced scroll handler
    this.debouncedScrollHandler = debounce(
      this.handleScroll.bind(this),
      this.scrollDebounceMs,
      {
        leading: true, // Trigger immediately on first call
        trailing: true, // Trigger after delay
        maxWait: 1000 // Maximum wait time
      }
    );
  }

  beforeUnmount() {
    // Cancel any pending debounced calls when component is destroyed
    if (this.debouncedScrollHandler) {
      this.debouncedScrollHandler.cancel();
    }
  }

  onOpenSelect(value: boolean) {
    if (value) {
      this.$emit("focus", "");
    }
    if (!value) {
      this.filterSearch = "";
      this.$emit("blur", "");
      this.showAllSelectedItems = false;
    }
    this.isOpenMenu = value;
  }

  modelDialog = false;

  // Computed properties for selected items display
  get displayedSelectedItems() {
    return this.filterShowValue.slice(0, this.maxDisplayedChips);
  }

  get hiddenSelectedItems() {
    return this.filterShowValue.slice(this.maxDisplayedChips);
  }

  get remainingSelectedCount() {
    return Math.max(0, this.filterShowValue.length - this.maxDisplayedChips);
  }

  // Improved key generation for better performance
  getItemKey(item: any, index: number): string {
    if (item[this.keyValue]) {
      return `item-${item[this.keyValue]}-${index}`;
    }
    return `item-${item}-${index}`;
  }

  getSelectedItemKey(item: any, index: number): string {
    if (item[this.keyValue]) {
      return `selected-${item[this.keyValue]}-${index}`;
    }
    return `selected-${item}-${index}`;
  }

  //logic filter search
  get selectedItemsName() {
    return this.modelValue.length > 0
      ? this.customSelectedItem
        ? `${this.modelValue.length} Status`
        : this.modelValue.map((item: any) => item[this.keyName]).join(", ")
      : "";
  }

  get displayAllText(): string {
    return this.customDisplayAll || this.selectAllText || "All";
  }

  filterSearch = "";

  private isAllText(value: any): boolean {
    const normalizedValue = String(value ?? "")
      .trim()
      .toLowerCase();
    return normalizedValue === "all" || normalizedValue === "semua";
  }

  private isAllItem(item: any): boolean {
    return (
      this.isAllText(item?.[this.keyValue] ?? item) ||
      this.isAllText(item?.[this.keyName])
    );
  }

  get dataOptions() {
    if (this.serverSide) {
      return this.options;
    }
    const tempOptions = this.options.filter((option: any) => {
      return option[this.keyName]
        ? option[this.keyName]
            .toLowerCase()
            .includes(this.filterSearch.toLowerCase())
        : option.toLowerCase().includes(this.filterSearch.toLowerCase());
    });
    if (this.allEnabled && !this.filterSearch) {
      const allObject: any = {};
      allObject[this.keyName] = "All";
      allObject[this.keyValue] = "ALL";
      tempOptions.unshift(allObject);
    }
    return tempOptions;
  }

  onSearch() {
    this.$emit("filter", this.filterSearch);
  }

  // Select All functionality
  get isAllSelected(): boolean {
    if (this.dataOptions.length === 0) return false;
    // Ditentukan murni berdasarkan apakah semua opsi yang dapat dipilih
    // sudah ada di dalam modelValue (tanpa memaksa state "ALL")
    // Check if all available options are selected
    const selectableOptions = this.dataOptions.filter(
      (option) =>
        !(
          this.disabledBosspack &&
          option[this.keyName]?.toLowerCase().includes("bosspack")
        ) && !this.isAllItem(option) // exclude "all" item dari count
    );

    if (selectableOptions.length === 0) return false;

    const selectedCount = selectableOptions.filter((option) =>
      this.isSelected(option)
    ).length;
    return selectedCount === selectableOptions.length;
  }

  get isPartialSelected(): boolean {
    if (this.dataOptions.length === 0) return false;

    const selectableOptions = this.dataOptions.filter(
      (option) =>
        !(
          this.disabledBosspack &&
          option[this.keyName]?.toLowerCase().includes("bosspack")
        )
    );

    const selectedCount = selectableOptions.filter((option) =>
      this.isSelected(option)
    ).length;
    return selectedCount > 0 && selectedCount < selectableOptions.length;
  }

  toggleSelectAll() {
    if (this.isAllSelected) {
      // Deselect all - clear semua item
      this.modelValue.splice(0, this.modelValue.length);
    } else {
      // Select all - clear dulu semua item yang ada
      this.modelValue.splice(0, this.modelValue.length);

      // Selalu gunakan format yang konsisten untuk "Semua"
      const allItem = {
        [this.keyName]: this.displayAllText,
        [this.keyValue]: "ALL"
      };

      // Hanya push satu item "Semua" saja
      if (this.selectAllOption && !this.allSelectedValueShow) {
        this.dataOptions
          .filter((item) => item[this.keyValue] !== "ALL")
          .forEach((item) => this.modelValue.push(item));
      } else {
        this.modelValue.push(allItem);
        this.dataOptions.forEach((item) => this.modelValue.push(item));
      }
    }

    this.$emit("change", this.modelValue);
    this.$emit("update:isError", !this.modelValue.length);
  }

  // logic for selected item
  isSelected(item: any) {
    // Handle special case for "all" item
    if (this.isAllItem(item) && this.allEnabled) {
      return this.isAll();
    }

    return this.modelValue.find((data: any) => {
      return data[this.keyValue]
        ? data[this.keyValue] === item[this.keyValue]
        : data === item;
    });
  }

  isAll() {
    // Cek jika ada item dengan value "all" (case insensitive)
    return this.modelValue.some((item: any) => this.isAllItem(item));
  }

  onChooseSelect(item: any, index: number, fromCheckbox = false) {
    // Handle special case for "all" item
    if (this.isAllItem(item) && this.allEnabled) {
      this.toggleSelectAll();
      return;
    }

    if (
      ((this.isAll() && !this.isAllItem(item)) ||
        (!this.isAll() &&
          this.modelValue.length > 0 &&
          this.isAllItem(item))) &&
      !this.allEnabled
    ) {
      this.modelValue.splice(0, this.modelValue.length);
      this.modelValue.push(item);
      this.$emit("change", [item]);
      this.$emit("update:modelValue", [item]);
      return;
    }

    if (this.isSelected(item)) {
      const indexItemSelected = this.modelValue.findIndex((data: any) =>
        data[this.keyValue]
          ? data[this.keyValue] === item[this.keyValue]
          : data === item
      );
      this.$emit("remove", item, index);
      this.modelValue.splice(indexItemSelected, 1);
      const allIndex = this.modelValue.findIndex((el: any) =>
        this.isAllItem(el)
      );
      if (allIndex !== -1) {
        this.modelValue.splice(allIndex, 1);
      }
    } else {
      // Jika memilih item individual saat "Semua" sudah terpilih, hapus "Semua" dulu
      if (this.isAll()) {
        this.modelValue.splice(0, this.modelValue.length);
      }
      this.modelValue.push(item);
    }

    this.$emit("change", this.modelValue);
    this.$emit("update:isError", !this.modelValue.length);

    if (this.closeOnSelecting && !fromCheckbox) {
      this.onOpenSelect(false);
    }
  }

  onChooseAll() {
    if (this.isAll()) {
      this.modelValue.splice(0, this.modelValue.length);
    } else {
      this.modelValue.splice(0, this.modelValue.length);
      // Gunakan format yang sama dengan toggleSelectAll
      const allItem = {
        [this.keyName]: this.displayAllText,
        [this.keyValue]: "ALL"
      };
      this.modelValue.push(allItem);
    }
    this.$emit("change", this.modelValue);
  }

  get filterShowValue(): any[] {
    const data = this.modelValue.slice(0, this.maxItemDisplayed);
    if (this.errorDataShow.length) {
      return data.map((item: any) => {
        const find = this.errorDataShow.find(
          (el: any) => el[this.keyValue] === item[this.keyValue]
        );
        if (find) {
          return { ...item, isError: true };
        }
        return item;
      });
    }
    return data;
  }

  onReset() {
    this.$emit("change", []);
    this.$emit("update:modelValue", []);
    this.$emit("reset", "");
  }

  // Improved infinite scroll with debounce from lodash
  onScroll(event: Event) {
    this.debouncedScrollHandler(event);
  }

  handleScroll(event: Event) {
    if (!this.useInfiniteScroll || this.isLoadingMore || !this.hasMoreData)
      return;

    const target = event.target as HTMLElement;
    const { scrollTop, scrollHeight, clientHeight } = target;

    // Trigger load more ketika mencapai 80% dari bottom
    const scrollThreshold = 50; // pixels from bottom
    if (scrollHeight - scrollTop - clientHeight <= scrollThreshold) {
      this.loadMore();
    }
  }

  async loadMore() {
    if (this.isLoadingMore || !this.hasMoreData) return;

    this.isLoadingMore = true;
    try {
      this.validateAllOnNextOptionsChange = true;
      await this.$emit("load-more", this.filterSearch);
    } finally {
      this.isLoadingMore = false;
    }
  }

  fetchNew({ target: { scrollTop } }: any) {
    if (scrollTop > 0 && this.useInfiniteScroll && this.hasMoreData) {
      this.$emit("scrolled", this.filterSearch);
    }
  }

  ellipsisString(val: any) {
    return ellipsisString(val, 15);
  }
}
